/**
 * Pricebook Modal
 *
 * Create/Edit pricebook form modal.
 * Standard pricebook: name, description, active. Firm pricebook: Firm, Partner, Use default vs Custom flag.
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useCreatePricebook, useUpdatePricebook } from '../../services/queries/pricebookQueries';
import { useAccounts, useContactsByAccount } from '../../services/queries/accountQueries';
import { useToast } from '../../contexts/ToastContext';
import { X } from 'lucide-react';
import type { Pricebook } from '../../types/index';

interface PricebookModalProps {
  isOpen: boolean;
  onClose: () => void;
  pricebook?: Pricebook | null;
}

export const PricebookModal: React.FC<PricebookModalProps> = ({
  isOpen,
  onClose,
  pricebook
}) => {
  const { success, error: showError } = useToast();
  const isEditMode = !!pricebook;

  const createPricebook = useCreatePricebook();
  const updatePricebook = useUpdatePricebook();
  const { data: accounts } = useAccounts();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isStandard: false,
    isActive: true,
    accountId: '',
    partnerId: '',
    useStandardPricebook: true
  });

  const { data: contactsForAccount } = useContactsByAccount(formData.accountId || undefined);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (pricebook) {
      setFormData({
        name: pricebook.name,
        description: pricebook.description || '',
        isStandard: pricebook.isStandard,
        isActive: pricebook.isActive,
        accountId: pricebook.accountId || '',
        partnerId: pricebook.partnerId || '',
        useStandardPricebook: pricebook.useStandardPricebook ?? true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        isStandard: false,
        isActive: true,
        accountId: '',
        partnerId: '',
        useStandardPricebook: true
      });
    }
    setErrors({});
  }, [pricebook, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'accountId') next.partnerId = '';
      return next;
    });
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Pricebook name is required';
    }
    if (!formData.isStandard) {
      if (!formData.accountId) newErrors.accountId = 'Firm is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    const pricebookData: Partial<Pricebook> = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      isStandard: formData.isStandard,
      isActive: formData.isActive
    };
    if (!formData.isStandard) {
      pricebookData.accountId = formData.accountId || undefined;
      pricebookData.partnerId = formData.partnerId || undefined;
      pricebookData.useStandardPricebook = formData.useStandardPricebook;
    } else {
      pricebookData.accountId = undefined;
      pricebookData.partnerId = undefined;
      pricebookData.useStandardPricebook = undefined;
    }

    if (isEditMode && pricebook) {
      updatePricebook.mutate(
        { id: pricebook.id, updates: pricebookData },
        {
          onSuccess: () => {
            success('Pricebook updated', `${pricebookData.name} has been updated`);
            onClose();
          },
          onError: () => {
            showError('Error', 'Failed to update pricebook');
          }
        }
      );
    } else {
      createPricebook.mutate(pricebookData as Omit<Pricebook, 'id' | 'createdDate' | 'lastModifiedDate' | 'createdBy'>, {
        onSuccess: () => {
          success('Pricebook created', `${pricebookData.name} has been created`);
          onClose();
        },
        onError: () => {
          showError('Error', 'Failed to create pricebook');
        }
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-[#1e293b] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Pricebook' : 'New Pricebook'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pricebook Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isEditMode && pricebook?.isStandard}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                    errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } ${isEditMode && pricebook?.isStandard ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                {isEditMode && pricebook?.isStandard && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Standard pricebook name cannot be changed</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white"
                />
              </div>

              {!isEditMode && (
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="isStandard"
                      checked={formData.isStandard}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Standard Pricebook</span>
                  </label>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Standard pricebooks are system defaults and cannot be deleted
                  </p>
                </div>
              )}

              {!formData.isStandard && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Firm <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="accountId"
                      value={formData.accountId}
                      onChange={handleChange}
                      disabled={isEditMode && !!pricebook?.accountId}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                        errors.accountId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } ${isEditMode && pricebook?.accountId ? 'opacity-70' : ''}`}
                    >
                      <option value="">Select firm</option>
                      {(accounts || []).filter(acc => acc.type === 'firm').map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                    {errors.accountId && <p className="mt-1 text-sm text-red-500">{errors.accountId}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Managing Partner
                    </label>
                    <select
                      name="partnerId"
                      value={formData.partnerId}
                      onChange={handleChange}
                      disabled={!formData.accountId || (isEditMode && !!pricebook?.partnerId)}
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#334155] text-gray-900 dark:text-white ${
                        errors.partnerId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } ${!formData.accountId ? 'opacity-50' : ''} ${isEditMode && pricebook?.partnerId ? 'opacity-70' : ''}`}
                    >
                      <option value="">Select partner</option>
                      {(contactsForAccount || []).map((c: { id: string; name?: string }) => (
                        <option key={c.id} value={c.id}>{c.name || c.id}</option>
                      ))}
                    </select>
                    {errors.partnerId && <p className="mt-1 text-sm text-red-500">{errors.partnerId}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pricing
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="useStandardPricebook"
                          checked={formData.useStandardPricebook}
                          onChange={() => setFormData(prev => ({ ...prev, useStandardPricebook: true }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Use default pricebook</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="useStandardPricebook"
                          checked={!formData.useStandardPricebook}
                          onChange={() => setFormData(prev => ({ ...prev, useStandardPricebook: false }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Custom pricebook for this firm</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#334155] border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-[#475569] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createPricebook.isPending || updatePricebook.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {createPricebook.isPending || updatePricebook.isPending
                  ? 'Saving...'
                  : isEditMode
                  ? 'Update Pricebook'
                  : 'Create Pricebook'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
